import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useHistory, useParams } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { IoPrintOutline } from "react-icons/io5";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import ClientsModal from "../ClientsCRUD/ClientsModal";
import { getUnitID } from "../../Auth/GetUnitCompany";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import AccountsAutocompleteInsert from "../../Components/AccountsAutocompleteInsert";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import PriceField from "../../Components/NumberFieldFormater";
import "./ReceivablesForm.scss";

const getDefaultDateForFiscalYear = (fiscalYear) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const minFiscalDate = `${fiscalYear}-01-01`;
  const maxFiscalDate = `${fiscalYear}-12-31`;

  if (todayIso < minFiscalDate) {
    return minFiscalDate;
  }

  if (todayIso > maxFiscalDate) {
    return maxFiscalDate;
  }

  return todayIso;
};

const createEmptySnapshot = (defaultDateForFiscalYear) => ({
  date: defaultDateForFiscalYear || "",
  invoiceNR: "",
  clientID: "",
  ledgerID: "",
  total: "",
  coment: "",
  paymentOptions: "",
});

function ReceivablesForm() {
  const history = useHistory();
  const { transactionId } = useParams();
  const isEditMode = Boolean(transactionId);
  const headers = useMemo(() => authHeader(), []);
  const email = LoginUserDetailed();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceNR, setInvoiceNR] = useState("");
  const [clientID, setClientID] = useState("");
  const [ledgerID, setLedgerID] = useState("");
  const [total, setTotal] = useState("");
  const [coment, setComent] = useState("");
  const [paymentOptions, setPaymentOptions] = useState("");
  const [unId, setUnID] = useState("");

  const [accounts, setAccounts] = useState([]);
  // const [filteredAccounts, setFilteredAccounts] = useState([]);

//   const [client, setClient] = useState([]);
  // const [filteredClient, setFilteredClient] = useState([]);

  const [ledgers, setLedgers] = useState([]);
  // const currentYear = new Date().getFullYear();

  const [insertingTransaction, setInsertingTransaction] = useState(false);
  const [isInitializing, setIsInitializing] = useState(isEditMode);
  const [savedTransfer, setSavedTransfer] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    date: "",
    clientID: "",
    ledgerID: "",
    paymentOptions: "",
    total: "",
    coment: "",
  });
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const defaultDateForFiscalYear = getDefaultDateForFiscalYear(fiscalYear);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(
    createEmptySnapshot(defaultDateForFiscalYear),
  );

  const currentFormSnapshot = {
    date: date || "",
    invoiceNR: String(invoiceNR || ""),
    clientID: String(clientID || ""),
    ledgerID: String(ledgerID || ""),
    total: String(total ?? ""),
    coment: String(coment || ""),
    paymentOptions: String(paymentOptions || ""),
  };

  const isFormDirty =
    JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot);

  const hasUnsavedData = isEditMode
    ? isFormDirty
    : Boolean(
        ledgerID ||
          paymentOptions ||
          total ||
          clientID ||
          coment.trim() ||
          (date && date !== defaultDateForFiscalYear),
      );

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);

    // Keep date inside current fiscal year bounds when fiscal year changes.
    setDate((previousDate) => {
      if (!previousDate) {
        return `${fiscalYear}-01-01`;
      }

      if (previousDate < `${fiscalYear}-01-01`) {
        return `${fiscalYear}-01-01`;
      }

      if (previousDate > `${fiscalYear}-12-31`) {
        return `${fiscalYear}-12-31`;
      }

      return previousDate;
    });
  }, [fiscalYear]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setInsertingTransaction(true);
    const nextErrors = {
      date: "",
      clientID: "",
      ledgerID: "",
      paymentOptions: "",
      total: "",
      coment: "",
    };

    if (!date) {
      nextErrors.date = "Data është e detyrueshme.";
    }
    if (!clientID) {
      nextErrors.clientID = "Ju lutem zgjidhni klientin.";
    }
    if (!ledgerID) {
      nextErrors.ledgerID = "Ju lutem zgjidhni llogarinë kontabël.";
    }
    if (!paymentOptions) {
      nextErrors.paymentOptions = "Ju lutem zgjidhni llogarinë e arkëtimit.";
    }
    if (!total || Number(total) <= 0) {
      nextErrors.total = "Ju lutem plotësoni vlerë valide më të madhe se 0.";
    }
    if (!coment || !coment.trim()) {
      nextErrors.coment = "Ju lutem plotësoni komentin.";
    }

    if (Object.values(nextErrors).some(Boolean)) {
      setValidationErrors(nextErrors);
      setInsertingTransaction(false);
      return;
    }

    setValidationErrors({
      date: "",
      clientID: "",
      ledgerID: "",
      paymentOptions: "",
      total: "",
      coment: "",
    });
    const stateDate = {
      Date: date,
      ActionType: "REC",
      InvoiceNr: invoiceNR,
      // AccountID: accountID,
      Total: total,
      SupplierID: clientID,
      LedgerID: ledgerID,
      PaymentOptions: paymentOptions,
      CompanyUnitID: unId > 0 ? unId : null,
      Coment: coment,
      ...(isEditMode
        ? {
            TransactionID: Number(transactionId),
            AccountID: clientID,
            ModifiedByUser: email,
          }
        : {
            CreatedByUser: email,
          }),
    };

    const request = isEditMode
      ? axios.put(ApiLink.API_URL + "Arketimet/update-receivables", stateDate, { headers })
      : axios.post(ApiLink.API_URL + "Arketimet", stateDate, { headers });

    request
      .then(() => {
        const debtorAccount = accounts.find(
          (account) => account.AccountID === Number(paymentOptions),
        );
        const creditorAccount = field;

        setSavedTransfer({
          ...stateDate,
          TransferNumber: `REC-${fiscalYear}-${invoiceNR}`,
          DebtorAccountName: debtorAccount?.AccountName || "",
          CreditorAccountName: creditorAccount?.AccountName || "",
          CreditorIdentification: creditorAccount?.IdentificationNumber || "",
        });

        handleSuccess(
          isEditMode
            ? "Receivables Updated Successfully"
            : "Receivables Added Successfully",
        );

        if (isEditMode) {
          setInsertingTransaction(false);
          history.push("/money/receivables");
          return;
        }

        setDate(defaultDateForFiscalYear);
        fetchNextInvoiceNumber();
        setLedgerID("");
        setClientID("");
        setComent("");
        setPaymentOptions("");
        setTotal("");
        // setClientName("");
        // setPaymentName("");
        setfield(null);
        setPayField(null);
        setInsertingTransaction(false);
        setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      })
      .catch((error) => {
        setInsertingTransaction(false);
        // console.error(error);
        if (error.response && error.response.status === 409) {
          setInsertingTransaction(false);
          errorAlert(error.response.data);
        } else {
          setInsertingTransaction(false);
          // errorAlert("An unknown error occurred while processing the request.");
        }
      });
  };

  const markDirty = () => {
    setSavedTransfer(null);
  };

  const clearValidationError = (field) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: "" };
    });
  };

  const fetchAccounts = async (ledgerID) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Accounts/accountByLedger",
        { headers, params: { ledger: ledgerID } },
      );
      setAccounts(response.data);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`,
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message} `);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const fetchLedger = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Ledger/Get_101_102_Ledgers",
        { headers },
      );
      setLedgers(response.data);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`,
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message} `);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

//   const fetchClient = async () => {
//     try {
//       const response = await axios.get(
//         ApiLink.API_URL + "Accounts/accountByLedger",
//         { headers, params: { ledger: 105 } },
//       );
//       setClient(response.data);
//     } catch (error) {
//       errorAlert("Gabim në kërkesë:", error);
//       if (
//         error.response &&
//         error.response.data &&
//         error.response.data.errorMessage
//       ) {
//         errorAlert(
//           `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`,
//         );
//       } else if (error.message) {
//         errorAlert(`Gabim: ${error.message} `);
//       } else {
//         errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
//       }
//     }
//   };
  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Arketimet/InvoiceNr",
        { headers, params: { fiscalYear: fiscalYear } },
      );
      setInvoiceNR(response.data.NextInvoiceNr);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`,
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Companies/getCompanyDetails",
        { headers },
      );

      if (Array.isArray(response?.data) && response.data.length > 0) {
        setCompanyDetails(response.data[0]);
      }
    } catch (error) {
      setCompanyDetails(null);
    }
  };

  // const [searchTerm, setSearchTerm] = useState("");
  // useEffect(() => {
  //     // Kërko nëse ka diçka të shkruar, në të kundërt shfaq të gjitha llogaritë
  //     setFilteredAccounts(
  //         accounts.filter((data) =>
  //             data.AccountName.toLowerCase().includes(searchTerm.toLowerCase())
  //         )
  //     );
  // }, [searchTerm, accounts]);

  // useEffect(() => {
  //     // Kërko nëse ka diçka të shkruar, në të kundërt shfaq të gjitha llogaritë
  //     setFilteredClient(
  //         client.filter((data) =>
  //             data.AccountName.toLowerCase().includes(searchTerm.toLowerCase())
  //         )
  //     );
  // }, [searchTerm, client]);

  // const handleInputChange = (newValue) => {
  //     const inputValue = newValue.replace(/\W/g, '');
  //     //setDebitLedgerID(inputValue);
  //     setSearchTerm(inputValue);
  // };

  // const [paymentName, setPaymentName] = useState("");
  // const handleSelectChange = (selectedOption) => {
  //     if (selectedOption && selectedOption.AccountID) {
  //         setPaymentOptions(selectedOption.AccountID);
  //         setPaymentName(selectedOption.label);
  //         // alert(selectedOption.label);
  //     } else {
  //         setPaymentOptions("");
  //         setPaymentName("");
  //     }
  // };

  // const [clientName, setClientName] = useState("");
  // const handleSelectClient = (selectedOption) => {
  //     if (selectedOption && selectedOption.AccountID) {
  //         setClientID(selectedOption.AccountID);
  //         setClientName(selectedOption.label);
  //         // alert(selectedOption.label);
  //     } else {
  //         setClientID("");
  //         setClientName("");
  //     }
  // };

  //Pjesa per select input field Clients
  const [field, setfield] = useState(null);
//   const handleAutocompleteChangeClient = (event, value) => {
//     if (value && value.AccountID) {
//       setClientID(value.AccountID);
//       setfield(value); // Përditëso `selectedArticle` me vlerën e zgjedhur
//     } else {
//       // Në rast se vlera është pastruar
//       setClientID(""); // Vendos vlerën e ArticleID në null
//       setfield(null); // Pastro `Autocomplete`
//     }
//   };

//   const defaultPropsClients = {
//     options: client,
//     getOptionLabel: (option) =>
//       `${option.IdentificationNumber} - ${option.AccountName}` || "",
//     renderOption: (props, option) => (
//       <>
//         <li {...props} style={{ borderBottom: "1px solid lightgray" }}>
//           <span>
//             {option.IdentificationNumber}
//             <br />
//             {option.AccountName}
//           </span>
//         </li>
//       </>
//     ),
//   };
  //////

  //Pjesa per select input field payment accounts
  const [payField, setPayField] = useState(null);
  const handleAutocompleteChangePayAccount = (event, value) => {
    if (value && value.AccountID) {
      setPaymentOptions(value.AccountID);
      setPayField(value);
      clearValidationError("paymentOptions");
      markDirty();
    } else {
      // Në rast se vlera është pastruar
      setPaymentOptions(""); // Vendos vlerën e ArticleID në null
      markDirty();
    }
  };

  const defaultPropsPayAccount = {
    options: accounts,
    getOptionLabel: (option) => option.AccountName || "",
  };

  useEffect(() => {
    getUnitID(headers).then(setUnID);
  }, [headers]);

  useEffect(() => {
    // fetchClient();
    fetchLedger();
    if (!isEditMode) {
      fetchNextInvoiceNumber();
    }
    fetchCompanyDetails();
    // fetchPaymentType();
    // fetchExpence()
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setDate(defaultDateForFiscalYear);
      setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      setIsInitializing(false);
      return;
    }

    let isCancelled = false;

    const loadEditData = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + "Arketimet", {
          headers,
          params: { fiscalYear },
        });

        const items = Array.isArray(response.data) ? response.data : [];
        const currentItem = items.find(
          (item) => Number(item.TransactionID) === Number(transactionId),
        );

        if (!currentItem) {
          throw new Error("Receivable not found");
        }

        const editDate = (currentItem.Date || "").substring(0, 10);
        const editInvoiceNr = String(currentItem.InvoiceNr || "");
        const editClientID = String(currentItem.AccountID || "");
        const editLedgerID = String(currentItem.LedgerID || "");
        const editTotal = String(currentItem.Total ?? "");
        const editComent = currentItem.Coment || "";
        const editPaymentOptions = String(currentItem.PaymentOptions || "");

        if (!isCancelled) {
          setDate(editDate);
          setInvoiceNR(editInvoiceNr);
          setClientID(editClientID);
          setLedgerID(editLedgerID);
          setTotal(editTotal);
          setComent(editComent);
          setPaymentOptions(editPaymentOptions);
          setfield(
            editClientID
              ? {
                  AccountID: Number(editClientID),
                  AccountName: currentItem.AccountName_AccountID || "",
                }
              : null,
          );
          setPayField(
            editPaymentOptions
              ? {
                  AccountID: Number(editPaymentOptions),
                  AccountName: currentItem.AccountName_PaymentOptions || "",
                }
              : null,
          );

          if (editLedgerID) {
            fetchAccounts(editLedgerID);
          }

          setInitialFormSnapshot({
            date: editDate,
            invoiceNR: editInvoiceNr,
            clientID: editClientID,
            ledgerID: editLedgerID,
            total: editTotal,
            coment: editComent,
            paymentOptions: editPaymentOptions,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          errorAlert("Gabim në marrjen e të dhënave për editim.");
          history.push("/money/receivables");
        }
      } finally {
        if (!isCancelled) {
          setIsInitializing(false);
        }
      }
    };

    loadEditData();

    return () => {
      isCancelled = true;
    };
  }, [
    isEditMode,
    transactionId,
    fiscalYear,
    headers,
    history,
    defaultDateForFiscalYear,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedData) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedData]);

  const handleClose = () => {
    if (hasUnsavedData) {
      setShowConfirmClose(true);
      return;
    }
    history.push("/money/receivables");
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    history.push("/money/receivables");
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDateDdMmYyyy = (isoDate) => {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      return isoDate || "";
    }

    const [year, month, day] = isoDate.split("-");
    return `${day}.${month}.${year}`;
  };

  const formatCurrencyValue = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) {
      return "0.00";
    }

    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const selectedLedger = ledgers.find((item) => item.LedgerID === Number(ledgerID));
  const selectedPaymentAccount = accounts.find(
    (item) => item.AccountID === Number(paymentOptions),
  );
  const isPrintReady = Boolean(
    date && ledgerID && paymentOptions && total && clientID && coment.trim(),
  );

  const sharedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f7fbf9",
      minHeight: "50px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d4e2db",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
      borderWidth: "2px",
    },
    "& .MuiInputLabel-root": {
      color: "#476057",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      paddingTop: "13px",
      paddingBottom: "13px",
    },
  };

  const sharedMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        border: "1px solid #dce9e2",
        borderRadius: "12px",
        boxShadow: "0 10px 24px rgba(31, 58, 54, 0.12)",
        maxHeight: 260,
        "& .MuiMenuItem-root": {
          fontSize: "14px",
          minHeight: 38,
        },
      },
    },
  };

  if (isInitializing) {
    return (
      <div className="RFM__page">
        <div className="RFM__screen" style={{ minHeight: "260px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "260px",
            }}
          >
            <CircularProgress size={28} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="RFM__page">
      <div className="RFM__screen print-hide">
        <div className="RFM__header">
          <div className="RFM__titleWrap">
            <h1 className="RFM__title">
              {isEditMode ? "Përditëso Arkëtimin" : "Arkëtim i ri"}
            </h1>
            <p className="RFM__subtitle">
              {isEditMode
                ? "Përditëso fushat kryesore dhe ruaj ndryshimet."
                : "Plotëso fushat kryesore dhe ruaj arkëtimin."}
            </p>
          </div>
          <div className="RFM__headerActions">
            <button
              type="button"
              className="RFM__btn RFM__btn--light"
              onClick={handlePrint}
              disabled={insertingTransaction || !isPrintReady}
            >
              <IoPrintOutline />
              Printo Mandatin
            </button>
            <button
              type="submit"
              form="receivables-form"
              className="RFM__btn RFM__btn--primary"
              disabled={insertingTransaction || !hasUnsavedData}
            >
              {insertingTransaction ? (
                <>
                  <CircularProgress style={{ color: "white", width: 18, height: 18 }} />
                  Duke ruajtur...
                </>
              ) : (
                <>{isEditMode ? "Përditëso" : "Ruaj & Ri"}</>
              )}
            </button>
            <button type="button" className="RFM__btn RFM__btn--light" onClick={handleClose}>
              <FaTimes />
              Mbyll
            </button>
          </div>
        </div>

        <div className="RFM__card">
          <div className="RFM__cardHeader">
            <div>
              <h2>{isEditMode ? "Përditësimi i Arkëtimit" : "Regjistrimi i Arkëtimit"}</h2>
              <p>
                {isEditMode
                  ? "Përditësoni të dhënat e kërkuara dhe ruani ndryshimet."
                  : "Plotësoni të dhënat e kërkuara për regjistrim të saktë dhe të plotë."}
              </p>
            </div>
            <div className="RFM__transferNo">REC-{fiscalYear}-{invoiceNR}</div>
          </div>

          <form id="receivables-form" className="RFM__form" onSubmit={handleSubmit} noValidate>
            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--invoice">
                <TextField
                  label="Numri i Arkëtimit"
                  value={`REC-${fiscalYear}-${invoiceNR}`}
                  disabled
                  fullWidth
                />
              </div>

              <div className="RFM__field RFM__field--date">
                <DateInputDdMmYyyy
                  label="Data"
                  value={date}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(nextIsoDate) => {
                    setDate(nextIsoDate);
                    clearValidationError("date");
                    markDirty();
                  }}
                />
                {validationErrors.date ? (
                  <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>
                    {validationErrors.date}
                  </div>
                ) : null}
              </div>

              <div className="RFM__field RFM__field--amount">
                <PriceField
                  label="Vlera"
                  name="total"
                  value={total}
                  onChange={(event) => {
                    setTotal(event.target.value ?? "");
                    clearValidationError("total");
                    markDirty();
                  }}
                  decimalScale={2}
                />
                {validationErrors.total ? (
                  <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>
                    {validationErrors.total}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--accounts">
              <section className="RFM__panel RFM__panel--debtor">
                <h3>Debitore (Destinacioni)</h3>

                <TextField
                  select
                  label="Llogaria kontabël Debitore"
                  value={ledgerID}
                  onChange={(event) => {
                    setLedgerID(event.target.value);
                    fetchAccounts(event.target.value);
                    clearValidationError("ledgerID");
                    clearValidationError("paymentOptions");
                    markDirty();
                  }}
                  fullWidth
                  size="small"
                  error={Boolean(validationErrors.ledgerID)}
                  helperText={validationErrors.ledgerID || ""}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ MenuProps: sharedMenuProps }}
                  sx={sharedFieldSx}
                >
                  {ledgers.map((data) => (
                    <MenuItem key={data.LedgerID} value={data.LedgerID}>
                      {data.LedgerNameEN}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  {...defaultPropsPayAccount}
                  noOptionsText="Nuk u gjet asnjë rezultat"
                  onChange={handleAutocompleteChangePayAccount}
                  value={payField}
                  isOptionEqualToValue={(option, value) => option.AccountID === value.AccountID}
                  ListboxProps={{
                    style: {
                      maxHeight: 220,
                      overflow: "auto",
                      paddingTop: 2,
                      paddingBottom: 2,
                    },
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid #edf3ef",
                        borderRadius: "8px",
                        marginBottom: "2px",
                        padding: "9px 10px",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>
                          {option.IdentificationNumber || "Pa ID"}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>
                          {option.AccountName}
                        </div>
                      </div>
                      <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Llogaria Debitore"
                      size="small"
                      error={Boolean(validationErrors.paymentOptions)}
                      helperText={validationErrors.paymentOptions || ""}
                      InputLabelProps={{ shrink: true }}
                      sx={sharedFieldSx}
                    />
                  )}
                />
              </section>

              <section className="RFM__panel RFM__panel--creditor">
                <h3>Kreditore (Burimi)</h3>

                <TextField
                  label="Llogaria kontabël Kreditore"
                  value="105-Klientet / LLA"
                  disabled
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={sharedFieldSx}
                />

                <div className="RFM__accountWithModal">
                  <div className="RFM__accountWithModalField">
                    <AccountsAutocompleteInsert
                      ledgerID={105}
                      textFieldWidth="100%"
                      marginFields="0"
                      value={field}
                      label="Kërko Klientin"
                      disableAutoFocus
                      onSelect={(account) => {
                        setClientID(account.AccountID);
                        setfield(account);
                        clearValidationError("clientID");
                        markDirty();
                      }}
                      onClear={() => {
                        setClientID("");
                        setfield(null);
                        markDirty();
                      }}
                      labelId="AccountID"
                    />
                  </div>
                  <IconButton
                    size="small"
                    onClick={() => setShowClientModal(true)}
                    aria-label="Shto klient"
                    title="Shto klient"
                    className="RFM__inlineAddBtn"
                  >
                    +
                  </IconButton>
                </div>
                {validationErrors.clientID ? (
                  <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>
                    {validationErrors.clientID}
                  </div>
                ) : null}
              </section>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--comment">
                <TextField
                  label="Komenti"
                  value={coment}
                  onChange={(event) => {
                    setComent(event.target.value);
                    clearValidationError("coment");
                    markDirty();
                  }}
                  multiline
                  minRows={3}
                  error={Boolean(validationErrors.coment)}
                  helperText={validationErrors.coment || ""}
                  placeholder="Shkruaj përshkrimin e arkëtimit..."
                  fullWidth
                />
              </div>
            </div>
          </form>
        </div>

        <ClientsModal
          show={showClientModal}
          onHide={() => setShowClientModal(false)}
          mode="insert"
          type="none"
        />
      </div>

      <div className="RFM__print print-only">
        <div className="RFM__printDoc">
          <div className="RFM__printHeader">
            <div className="RFM__printBrand">
              {companyDetails?.CompanyLogo ? (
                <div className="RFM__printLogoWrap">
                  <img
                    src={ApiLink.PHOTO_URL + companyDetails.CompanyLogo}
                    alt="Company logo"
                    className="RFM__printLogo"
                  />
                </div>
              ) : null}
              <h2>{companyDetails?.CompanyName || "Kompania"}</h2>
              <p>{companyDetails?.Address || "-"}</p>
              <div className="RFM__printCompanyMeta">
                <span>NUI: {companyDetails?.IdentificationNumber || "-"}</span>
                <span>Telefon: {companyDetails?.PhoneNumbers || "-"}</span>
              </div>
            </div>
            <div className="RFM__printTitleBox">
              <h1>MANDAT ARKËTIMI</h1>
              <p>Dokument financiar</p>
            </div>
          </div>

          <div className="RFM__printMetaStrip">
            <div className="RFM__printMetaItem">
              <span>Nr. dokumentit</span>
              <strong className="RFM__mono">{savedTransfer?.TransferNumber || `REC-${fiscalYear}-${invoiceNR}`}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e dokumentit</span>
              <strong>{formatDateDdMmYyyy(savedTransfer?.Date || date)}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Viti fiskal</span>
              <strong>{fiscalYear}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e printimit</span>
              <strong>{formatDateDdMmYyyy(new Date().toISOString().slice(0, 10))}</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Palët e Transaksionit</div>
          <div className="RFM__printPartyGrid">
            <div className="RFM__printPartyCard">
              <h3>Debitore (Destinacioni)</h3>
              <div className="RFM__printPartyRow">
                <span>Llogaria kontabël</span>
                <strong>{selectedLedger?.LedgerNameEN || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Llogaria e arkëtimit</span>
                <strong>{savedTransfer?.DebtorAccountName || selectedPaymentAccount?.AccountName || payField?.AccountName || "-"}</strong>
              </div>
            </div>

            <div className="RFM__printPartyCard">
              <h3>Kreditore (Burimi)</h3>
              <div className="RFM__printPartyRow">
                <span>Klienti</span>
                <strong>{savedTransfer?.CreditorAccountName || field?.AccountName || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Nr. identifikues</span>
                <strong>{savedTransfer?.CreditorIdentification || field?.IdentificationNumber || "-"}</strong>
              </div>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Detajet Financiare</div>
          <div className="RFM__printTable">
            <div className="RFM__printTableRow">
              <span>Vlera totale e arkëtimit</span>
              <strong>{formatCurrencyValue(savedTransfer?.Total || total)} EUR</strong>
            </div>
            <div className="RFM__printTableRow">
              <span>Mënyra e arkëtimit</span>
              <strong>{savedTransfer?.DebtorAccountName || selectedPaymentAccount?.AccountName || payField?.AccountName || "-"}</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Përshkrimi</div>
          <div className="RFM__printCommentBox RFM__printCommentBox--doc">
            {savedTransfer?.Coment || coment || "-"}
          </div>

          <div className="RFM__printSignatureRow">
            <div className="RFM__printSignature">
              <span>Përpiloi</span>
              <div className="RFM__printSignatureLine" />
            </div>
            <div className="RFM__printSignature">
              <span>Pranoi</span>
              <div className="RFM__printSignatureLine" />
            </div>
          </div>

          <div className="RFM__printFooterNote">
            Dokument i gjeneruar elektronikisht dhe i vlefshëm për evidencë kontabël.
          </div>
          <div className="RFM__printCopyright">
            Copyright © {new Date().getFullYear()} Accountree. Të gjitha të drejtat e rezervuara.
          </div>
        </div>
      </div>

      {showConfirmClose && (
        <div className="RFM__modalOverlay">
          <div className="RFM__modal">
            <h3>Kujdes</h3>
            <p>Keni ndryshime të paruajtura. A dëshironi të mbyllet forma pa ruajtur?</p>
            <div className="RFM__modalActions">
              <button type="button" className="RFM__btn RFM__btn--light" onClick={() => setShowConfirmClose(false)}>
                Jo, qëndro
              </button>
              <button type="button" className="RFM__btn RFM__btn--danger" onClick={confirmClose}>
                Po, mbylle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceivablesForm;
